import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Upload,
  ChevronRight,
  FileCheck,
  AlertCircle,
  Loader2,
  Plus,
  Eye,
  Mail,
  PenTool,
  Clock,
  CheckCircle,
  Send
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

// Tax form templates
const TAX_FORMS = [
  {
    id: "w9",
    name: "Form W-9",
    description: "Request for Taxpayer Identification Number and Certification",
    fields: ["name", "business_name", "tax_classification", "address", "city", "state", "zip", "ssn_or_ein"]
  },
  {
    id: "w8ben",
    name: "Form W-8BEN",
    description: "Certificate of Foreign Status of Beneficial Owner",
    fields: ["name", "country", "address", "city", "foreign_tin", "date_of_birth"]
  },
  {
    id: "w8bene",
    name: "Form W-8BEN-E",
    description: "Certificate of Status of Beneficial Owner for Entities",
    fields: ["entity_name", "country", "entity_type", "address", "ein"]
  },
  {
    id: "w4",
    name: "Form W-4",
    description: "Employee's Withholding Certificate",
    fields: ["name", "ssn", "address", "filing_status", "dependents"]
  },
];

export default function TaxHubPage({ user }) {
  const [taxForms, setTaxForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("tax-forms");
  const [formTab, setFormTab] = useState("all");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [showQuickLook, setShowQuickLook] = useState(false);
  const [quickLookForm, setQuickLookForm] = useState(null);
  
  // Email and Signature Request states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [emailData, setEmailData] = useState({ recipient_email: "", recipient_name: "", message: "" });
  const [sigData, setSigData] = useState({ recipient_email: "", recipient_name: "", message: "", expires_days: 7 });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);

  // Form data for generation
  const [formData, setFormData] = useState({
    name: user?.name || "",
    business_name: "",
    tax_classification: "individual",
    address: "",
    city: "",
    state: "",
    zip: "",
    ssn_or_ein: "",
    country: "United States",
    foreign_tin: "",
    date_of_birth: "",
    entity_name: "",
    entity_type: "",
    ein: "",
    ssn: "",
    filing_status: "single",
    dependents: "0"
  });

  useEffect(() => {
    fetchTaxForms();
    fetchSignatureRequests();
  }, []);

  const fetchTaxForms = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/tax-forms`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setTaxForms(data);
      }
    } catch (err) {
      console.error("Error fetching tax forms:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatureRequests = async () => {
    setLoadingSignatures(true);
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/tax-forms/signature-requests`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setSignatureRequests(data);
      }
    } catch (err) {
      console.error("Error fetching signature requests:", err);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const handleEmailForm = (form) => {
    setSelectedForm(form);
    setEmailData({ recipient_email: "", recipient_name: "", message: "" });
    setShowEmailModal(true);
  };

  const handleRequestSignature = (form) => {
    setSelectedForm(form);
    setSigData({ recipient_email: "", recipient_name: "", message: "", expires_days: 7 });
    setShowSignatureModal(true);
  };

  const sendEmail = async () => {
    if (!emailData.recipient_email) {
      toast.error("Please enter recipient email");
      return;
    }
    
    setSendingEmail(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/tax-forms/${selectedForm.form_id}/email`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          form_id: selectedForm.form_id,
          ...emailData
        })
      });
      
      if (res.ok) {
        toast.success(`Form sent to ${emailData.recipient_email}`);
        setShowEmailModal(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendSignatureRequest = async () => {
    if (!sigData.recipient_email || !sigData.recipient_name) {
      toast.error("Please enter recipient email and name");
      return;
    }
    
    setSendingSignature(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/tax-forms/${selectedForm.form_id}/request-signature`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          form_id: selectedForm.form_id,
          ...sigData
        })
      });
      
      if (res.ok) {
        toast.success(`Signature request sent to ${sigData.recipient_email}`);
        setShowSignatureModal(false);
        fetchSignatureRequests();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send signature request");
      }
    } catch (err) {
      toast.error("Failed to send signature request");
    } finally {
      setSendingSignature(false);
    }
  };

  const handleSelectForm = async (form) => {
    setSelectedFormType(form);
    
    // Fetch company profile to auto-fill form
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/company-profile`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const profile = await response.json();
        setFormData(prev => ({
          ...prev,
          name: user?.name || prev.name,
          business_name: profile.company_name || prev.business_name,
          address: profile.address || prev.address,
          city: profile.city || prev.city,
          state: profile.state || prev.state,
          zip: profile.zip || prev.zip,
          ein: profile.ein || prev.ein,
        }));
      }
    } catch (err) {
      console.error("Error fetching company profile:", err);
    }
    
    setShowGenerateModal(true);
  };

  const handleGenerateForm = async (e) => {
    e.preventDefault();
    setGenerating(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      // Use the new fill endpoint that uses actual IRS forms
      const res = await fetch(`${API_URL}/api/tax-forms/fill`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          form_type: selectedFormType.id,
          form_data: formData
        })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`${selectedFormType.name} generated successfully!`);
        setShowGenerateModal(false);
        fetchTaxForms();
        
        // Auto-download the generated form
        if (result.form_id) {
          handleDownloadForm(result.form_id);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate form");
      }
    } catch (err) {
      toast.error("Failed to generate form");
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadForm = async (formId) => {
    setDownloading(formId);
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/tax-forms/${formId}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tax-form-${formId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Form downloaded!");
      } else {
        toast.error("Failed to download form");
      }
    } catch (err) {
      toast.error("Failed to download form");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadBlankForm = async (formType) => {
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/irs-forms/${formType}/download`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `IRS_Form_${formType.toUpperCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Blank IRS ${formType.toUpperCase()} downloaded!`);
      } else {
        toast.error("Failed to download form");
      }
    } catch (err) {
      toast.error("Failed to download form");
    }
  };

  const filteredForms = taxForms.filter(form => {
    if (formTab === "sent") return form.direction === "sent";
    if (formTab === "received") return form.direction === "received";
    return true;
  });

  const renderFormFields = () => {
    if (!selectedFormType) return null;

    switch (selectedFormType.id) {
      case "w9":
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name (as shown on tax return) *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Business Name (if different)</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tax Classification *</Label>
              <Select value={formData.tax_classification} onValueChange={(v) => setFormData(prev => ({ ...prev, tax_classification: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual/Sole Proprietor</SelectItem>
                  <SelectItem value="c_corp">C Corporation</SelectItem>
                  <SelectItem value="s_corp">S Corporation</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="trust">Trust/Estate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street address"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP *</Label>
                <Input
                  value={formData.zip}
                  onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SSN or EIN *</Label>
              <Input
                value={formData.ssn_or_ein}
                onChange={(e) => setFormData(prev => ({ ...prev, ssn_or_ein: e.target.value }))}
                placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                required
              />
            </div>
          </>
        );

      case "w8ben":
        return (
          <>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country of Citizenship *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Permanent Residence Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Foreign Tax ID Number</Label>
              <Input
                value={formData.foreign_tin}
                onChange={(e) => setFormData(prev => ({ ...prev, foreign_tin: e.target.value }))}
              />
            </div>
          </>
        );

      case "w4":
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Social Security Number *</Label>
                <Input
                  value={formData.ssn}
                  onChange={(e) => setFormData(prev => ({ ...prev, ssn: e.target.value }))}
                  placeholder="XXX-XX-XXXX"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Filing Status *</Label>
                <Select value={formData.filing_status} onValueChange={(v) => setFormData(prev => ({ ...prev, filing_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single or Married filing separately</SelectItem>
                    <SelectItem value="married">Married filing jointly</SelectItem>
                    <SelectItem value="head">Head of household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Number of Dependents</Label>
                <Input
                  type="number"
                  value={formData.dependents}
                  onChange={(e) => setFormData(prev => ({ ...prev, dependents: e.target.value }))}
                  min="0"
                />
              </div>
            </div>
          </>
        );

      default:
        return (
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
        );
    }
  };

  return (
    <DashboardLayout user={user}>
      <div className="space-y-4 md:space-y-6">
        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <div className="overflow-x-auto -mx-3 px-3">
            <TabsList className="bg-transparent border-b border-gray-200 inline-flex min-w-max justify-start rounded-none p-0 h-auto">
              <TabsTrigger
                value="tax-forms"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563eb] data-[state=active]:bg-transparent px-3 md:px-4 pb-3 text-sm md:text-base whitespace-nowrap"
              >
                Tax Forms
              </TabsTrigger>
              <TabsTrigger
                value="signature-requests"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563eb] data-[state=active]:bg-transparent px-3 md:px-4 pb-3 text-sm md:text-base whitespace-nowrap"
              >
                Signatures
                {signatureRequests.filter(r => r.status === "pending").length > 0 && (
                  <Badge className="ml-1.5 bg-purple-100 text-purple-700 text-xs">
                    {signatureRequests.filter(r => r.status === "pending").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="1099-filing"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563eb] data-[state=active]:bg-transparent px-3 md:px-4 pb-3 text-sm md:text-base whitespace-nowrap"
              >
                1099 Filing
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tax Forms Tab */}
          <TabsContent value="tax-forms" className="mt-4 md:mt-6 space-y-4 md:space-y-6">
            {/* Tax Form Cards */}
            <div className="space-y-3 md:space-y-4">
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Generate Tax Forms</h2>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {TAX_FORMS.map((form) => (
                  <Card
                    key={form.id}
                    className="border border-gray-200 hover:border-[#2563eb] hover:shadow-md transition-all group overflow-hidden"
                    data-testid={`tax-form-${form.id}`}
                  >
                    <CardContent className="p-0">
                      <div className="h-20 md:h-32 bg-gray-100 flex items-center justify-center border-b border-gray-200">
                        <div className="w-14 h-20 md:w-20 md:h-28 bg-white shadow-sm rounded border border-gray-200 flex items-center justify-center">
                          <FileText className="w-6 h-6 md:w-8 md:h-8 text-gray-400" />
                        </div>
                      </div>
                      <div className="p-2 md:p-3 space-y-1 md:space-y-2">
                        <p className="text-xs md:text-sm font-semibold text-gray-900 line-clamp-1">{form.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-2 hidden md:block">{form.description}</p>
                        <div className="flex gap-1.5 md:gap-2 pt-1">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-xs h-7 md:h-8 px-2"
                            onClick={() => handleSelectForm(form)}
                          >
                            <span className="hidden md:inline">Fill & </span>Generate
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7 md:h-8 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadBlankForm(form.id);
                            }}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Generated Forms List */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Your Tax Forms</h2>
                <Tabs value={formTab} onValueChange={setFormTab}>
                  <TabsList className="bg-gray-100">
                    <TabsTrigger value="all" className="data-[state=active]:bg-white">All</TabsTrigger>
                    <TabsTrigger value="sent" className="data-[state=active]:bg-white">Sent</TabsTrigger>
                    <TabsTrigger value="received" className="data-[state=active]:bg-white">Received</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {filteredForms.length === 0 ? (
                <Card className="border border-gray-200">
                  <CardContent className="py-12">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <FileText className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="font-medium text-gray-900">No tax forms yet</p>
                      <p className="text-sm text-gray-500 mt-1">Generate your first tax form above</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-gray-200 overflow-hidden">
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Form</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Year</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Created</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredForms.map((form) => (
                          <tr key={form.form_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-900">{form.form_type?.toUpperCase()}</span>
                                {form.form_type?.includes("SIGNED") && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Signed</Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{form.year}</td>
                            <td className="px-4 py-3">
                              <Badge className={
                                form.status === "completed" ? "bg-green-100 text-green-700" :
                                form.status === "signed" ? "bg-green-100 text-green-700" :
                                form.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                "bg-gray-100 text-gray-700"
                              }>
                                {form.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-sm">
                              {form.created_at ? new Date(form.created_at).toLocaleDateString() : "-"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setQuickLookForm(form); setShowQuickLook(true); }}
                                  title="Quick Look"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadForm(form.form_id)}
                                  disabled={downloading === form.form_id}
                                  title="Download"
                                >
                                  {downloading === form.form_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEmailForm(form)}
                                  title="Email Form"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                {!form.form_type?.includes("SIGNED") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRequestSignature(form)}
                                    title="Request Signature"
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  >
                                    <PenTool className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Signature Requests Tab */}
          <TabsContent value="signature-requests" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Signature Requests</h2>
              <Button variant="outline" onClick={fetchSignatureRequests} disabled={loadingSignatures}>
                {loadingSignatures ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </div>

            {loadingSignatures ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : signatureRequests.length === 0 ? (
              <Card className="border border-gray-200">
                <CardContent className="py-16">
                  <div className="text-center max-w-md mx-auto">
                    <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <PenTool className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Signature Requests</h3>
                    <p className="text-gray-500">
                      When you request signatures on tax forms, they will appear here.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-gray-200 overflow-hidden">
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Form</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {signatureRequests.map((req) => (
                        <tr key={req.signature_request_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <PenTool className="w-4 h-4 text-purple-500" />
                              <span className="font-medium">{req.form_type?.toUpperCase()}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{req.recipient_name}</p>
                              <p className="text-sm text-gray-500">{req.recipient_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              req.status === "signed" ? "bg-green-100 text-green-700" :
                              req.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              req.status === "expired" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-700"
                            }>
                              {req.status === "signed" && <CheckCircle className="w-3 h-3 mr-1" />}
                              {req.status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                              {req.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {req.expires_at ? new Date(req.expires_at).toLocaleDateString() : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* 1099 Filing Tab */}
          <TabsContent value="1099-filing" className="mt-6">
            <Card className="border border-gray-200">
              <CardContent className="py-16">
                <div className="text-center max-w-md mx-auto">
                  <div className="w-16 h-16 bg-[#fef3c7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="w-8 h-8 text-[#d97706]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">1099 E-Filing</h3>
                  <p className="text-gray-500 mb-6">
                    Automatically generate and e-file 1099 forms for your contractors.
                  </p>
                  <Button className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                    Start 1099 Filing
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card className="border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Automatic Generation</h4>
                      <p className="text-sm text-gray-500 mt-1">1099-NEC forms auto-generated from your contractor payments.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileCheck className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">IRS E-Filing</h4>
                      <p className="text-sm text-gray-500 mt-1">Direct submission to IRS with confirmation tracking.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Deadline Reminders</h4>
                      <p className="text-sm text-gray-500 mt-1">Get notified before filing deadlines.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Generate Form Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate {selectedFormType?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateForm} className="space-y-4">
            {renderFormFields()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGenerateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={generating} className="bg-[#2563eb]">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Generate PDF
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tax Form Quick Look Modal */}
      <Dialog open={showQuickLook} onOpenChange={setShowQuickLook}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col rounded-2xl border-2 border-[#BFFF00]/30 p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#BFFF00]/20 bg-[#F7FFE5]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#121212] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#BFFF00]" />
              </div>
              <div>
                <h3 className="font-bold text-[#121212]">
                  {quickLookForm?.form_type?.toUpperCase()} - {quickLookForm?.pdf_filename}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className="rounded-full bg-green-100 text-green-700">
                    {quickLookForm?.status || "generated"}
                  </Badge>
                  <span className="text-sm text-[#121212]/60">
                    {quickLookForm?.created_at ? new Date(quickLookForm.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => quickLookForm && handleDownloadForm(quickLookForm.form_id)}
                disabled={downloading === quickLookForm?.form_id}
                className="rounded-full border-[#BFFF00]/50"
              >
                {downloading === quickLookForm?.form_id ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-1" />
                )}
                Download
              </Button>
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex-1 bg-gray-100 overflow-hidden" style={{ minHeight: '500px' }}>
            {quickLookForm?.form_id && (
              <object
                data={`${API_URL}/api/tax-forms/${quickLookForm.form_id}/download`}
                type="application/pdf"
                className="w-full h-full"
                style={{ minHeight: '500px' }}
              >
                <div className="h-full flex items-center justify-center">
                  <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                    <FileText className="w-12 h-12 text-[#121212]/40 mx-auto mb-3" />
                    <h4 className="font-bold text-lg text-[#121212] mb-2">PDF Preview</h4>
                    <p className="text-[#121212]/60 text-sm mb-4">Your browser cannot display this PDF inline.</p>
                    <Button
                      onClick={() => quickLookForm && handleDownloadForm(quickLookForm.form_id)}
                      className="rounded-full bg-[#121212]"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Form Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email Tax Form
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Send <strong>{selectedForm?.form_type?.toUpperCase()}</strong> form via email
              </p>
            </div>
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={emailData.recipient_email}
                onChange={(e) => setEmailData(prev => ({ ...prev, recipient_email: e.target.value }))}
                placeholder="recipient@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Recipient Name</Label>
              <Input
                value={emailData.recipient_name}
                onChange={(e) => setEmailData(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                value={emailData.message}
                onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Add a personal message..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button 
              onClick={sendEmail} 
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Email</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-purple-600" />
              Request Digital Signature
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Send <strong>{selectedForm?.form_type?.toUpperCase()}</strong> for digital signature
              </p>
            </div>
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={sigData.recipient_email}
                onChange={(e) => setSigData(prev => ({ ...prev, recipient_email: e.target.value }))}
                placeholder="signer@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Recipient Name *</Label>
              <Input
                value={sigData.recipient_name}
                onChange={(e) => setSigData(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="Full Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                value={sigData.message}
                onChange={(e) => setSigData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Please review and sign this form..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Expires In</Label>
              <Select 
                value={String(sigData.expires_days)} 
                onValueChange={(v) => setSigData(prev => ({ ...prev, expires_days: parseInt(v) }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
            <Button 
              onClick={sendSignatureRequest} 
              disabled={sendingSignature}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingSignature ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <><PenTool className="w-4 h-4 mr-2" /> Send Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
